import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../context/app-context';
import { useSnackbar } from 'notistack';
import { Loader } from '../../elements/loader';
import { StoreWithDetails } from '../../types/vendor-menu';
import { IOModelVenueWithStores } from '../../types/io-models';
import { ServiceMethod } from '../../types/order';
import { useTranslation } from 'react-i18next';
import { Image } from '../../elements/image';
import {
  IVenueSectionDeliveryInfoMeta,
  VenueSeatChoice,
  VenueTypes,
} from './home';
import { Box, Grid, Stack } from '@mui/material';
import { OrderTypeStatusBar } from '../../components/order-type-status';
import { images } from '../../constants/images';
import { CustomSelect } from '../../elements/select';
import { PMSGuestInfoForm } from '../../components/patron-capture-details-forms/pms-guest-info-form';
import { VenueDetailsCaptureForm } from '../../components/patron-capture-details-forms/venue-details-capture-form';

export const VenueHomePage: React.FC<{
  patronVenueChoices: VenueSeatChoice | undefined;
  venue: IOModelVenueWithStores;
  vendors: StoreWithDetails[];
  refetchVenue: () => void;
  refetchVendor: () => void;
  goNext: (
    selectedVenue: IOModelVenueWithStores,
    selectedServiceMethod: ServiceMethod | undefined,
    desiredDeliveryLocInfo: IVenueSectionDeliveryInfoMeta,
    selectedStore?: StoreWithDetails,
    guestInfo?: any,
  ) => void;
}> = ({
  venue,
  vendors,
  patronVenueChoices,
  goNext,
  refetchVenue,
  refetchVendor,
}) => {
  const appContext = useContext(AppContext);

  const [isVenueAvailable, setVenueAvailability] = useState(false);
  const { t } = useTranslation(['patron_portal', 'common']);

  const [hasCustomForm, setHasCustomForm] = useState<boolean>();
  const [venuePMSIntegrationID, setPMSIntegrationID] = useState<string>();

  const [draftPatronVenueChoice, setPatronVenueChoice] =
    useState<VenueSeatChoice>({
      venueID: undefined,
      serviceChoices: {
        serviceMethod: undefined,
        deliveryInfo: {},
      },
    });
  const [brandingImageURI, setBrandingImage] = useState<string>(
    appContext.whitelabelConfig?.value?.landingImageUrl ||
      images.DEFAULT_LANDING_PAGE_IMAGE,
  );
  const [selectedServiceMethod, setSelectedServiceMethod] =
    useState<ServiceMethod>();

  const defaultVenueLandingImage = useMemo(() => {
    return venue.patronNavigationImageUrl
      ? venue.patronNavigationImageUrl
      : appContext.whitelabelConfig?.value?.landingImageUrl ||
          images.DEFAULT_LANDING_PAGE_IMAGE;
  }, [venue]);

  useEffect(() => {
    if (venue) {
      if (
        venue.venueType === VenueTypes.HOTEL &&
        venue.venueIntegrations &&
        venue.venueIntegrations.length
      ) {
        if (venue?.venueIntegrations[0].meta?.platformMeta?.hotelID) {
          setHasCustomForm(true);
          setPMSIntegrationID(venue.venueIntegrations[0].id);
        }
      }
      switch (selectedServiceMethod) {
        case ServiceMethod.COLLECT_FROM_VENDOR:
          setBrandingImage(
            venue.cncBrandingImageUrl
              ? venue.cncBrandingImageUrl
              : defaultVenueLandingImage,
          );
          break;
        case ServiceMethod.DELIVERY:
          setBrandingImage(
            venue.ttsBrandingImageUrl
              ? venue.ttsBrandingImageUrl
              : defaultVenueLandingImage,
          );
          break;
        default:
          setBrandingImage(defaultVenueLandingImage);
      }

      setVenueAvailability(!venue.isDisabled);
    } else {
      setVenueAvailability(false);
    }
  }, [venue, vendors, selectedServiceMethod]);

  // Methods

  const onSeatSelectionChange = (
    selectedServiceMethod: ServiceMethod | undefined,
    desiredDeliveryLocInfo: IVenueSectionDeliveryInfoMeta,
    selectedStore?: StoreWithDetails,
    guestInfo?: any,
  ) => {
    if (venue) {
      goNext(
        venue,
        selectedServiceMethod,
        desiredDeliveryLocInfo,
        selectedStore,
        guestInfo,
      );
    }
  };

  const onCustomFormSubmit = (
    selectedServiceMethod: ServiceMethod,
    deliveryLocationInfo: IVenueSectionDeliveryInfoMeta,
    guestInfo: any,
    selectedStore?: StoreWithDetails,
  ) => {
    // todo: Save Guest Info in context (TO be used when signing-in Patron)
    // goToNext(venue, selectedServiceMethod, deliveryLocationInfo);
  };

  const getSeatSelectionForm = (venue: IOModelVenueWithStores) => {
    if (
      venue.venueType === VenueTypes.HOTEL &&
      hasCustomForm &&
      venuePMSIntegrationID
    ) {
      return (
        <PMSGuestInfoForm
          venueID={venue.id}
          integrationID={venuePMSIntegrationID}
          onLocationSelection={onCustomFormSubmit}
          selectedServiceMethod={selectedServiceMethod}
          setSelectedServiceMethod={setSelectedServiceMethod}
        ></PMSGuestInfoForm>
      );
    } else {
      return (
        <VenueDetailsCaptureForm
          venue={venue}
          serviceAvailable={isVenueAvailable}
          serviceChoices={{
            serviceMethod: selectedServiceMethod,
            deliveryInfo: {
              section:
                patronVenueChoices?.serviceChoices?.deliveryInfo?.sectionID,
              collectionPoint:
                patronVenueChoices?.serviceChoices?.deliveryInfo
                  ?.collectionPointID,
              row: patronVenueChoices?.serviceChoices?.deliveryInfo?.row,
              seat: patronVenueChoices?.serviceChoices?.deliveryInfo?.seat,
              deliveryArea: patronVenueChoices?.serviceChoices?.deliveryInfo?.deliveryArea
            },
          }}
          onLocationSelection={onSeatSelectionChange}
          vendors={vendors}
          refetchVendor={refetchVendor}
          selectedServiceMethod={selectedServiceMethod}
          setSelectedServiceMethod={setSelectedServiceMethod}
        />
      );
    }
  };

  if (venue) {
    return (
      <Grid container>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              minHeight: { md: '100vh' },
              justifyContent: 'center',
              display: 'flex',
            }}
          >
            <Image src={brandingImageURI} alt={'Venue Image'} />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          {/* TODO: Muting Order status banner */}
          <OrderTypeStatusBar
            serviceAvailable={isVenueAvailable}
            venueName={venue?.name}
            serviceMethod={
              patronVenueChoices?.serviceChoices?.serviceMethod ||
              ServiceMethod.DELIVERY
            }
          />
          <Stack sx={{ bottom: 0 }} spacing={2} py={2} px={2}>
            {/* {venue.isDisabled || venue.sellingStores?.length < 1 ? (
              <Alert severity="error">
                {t('patron_portal.vendorListing.notOperatingMsg', {
                  ns: 'patron_portal',
                  venueName: venue.name,
                })}
              </Alert>
            ) : null} */}

            <CustomSelect
              onChange={(value: string) => {
                // setVenue(value);
              }}
              title={t('common.location', { ns: 'common' })}
              value={venue.id}
              data={[{ id: venue.id, label: venue.name }]}
              disabled
            />

            {venue && getSeatSelectionForm(venue)}
          </Stack>
        </Grid>
      </Grid>
    );
  }
  return <Loader visible={true} />;
};
